import { useSearch } from '@tanstack/react-router';
import { useCampaignCapabilitiesStore, useCampaignUiStore, type ShareGrantDto } from '@discovr/features';
import { buildCampaignShareUrl } from './buildCampaignShareUrl';
import { buildCampCode, buildShortSharePath, PANEL_BY_PURPOSE, type ShortShareInput } from './shortLink';
import type { CampaignSearch } from '../search';

// Placeholder campaign code used only to drive buildShortSharePath's path
// grammar — never sent anywhere. Keeping shareInputTail a thin wrapper around
// the one real encoder avoids a second, drift-prone tail-building implementation.
const TAIL_PLACEHOLDER_CODE = '_';

/**
 * Strips the `/s/{code}/` prefix off buildShortSharePath's output, leaving just
 * the `tail` the BE's mint endpoint (POST .../share-grants) and the public
 * share-view lookup parse — e.g. `p2/scripts/in-review` or `mkbhd/script`.
 */
export function shareInputTail(input: ShortShareInput): string {
  const path = buildShortSharePath(TAIL_PLACEHOLDER_CODE, input);
  return path.slice(`/s/${TAIL_PLACEHOLDER_CODE}/`.length);
}

// `undefined` and `''` both mean "no sub/pill" — normalize so a grant minted
// before a field existed still matches an input that omits it.
function normStr(v?: string): string {
  return v ?? '';
}

// The p{N} token always defaults to page 1 (see PAGINATED_TABS in shortLink.ts),
// so an absent/1 `page` on either side must compare equal.
function normPage(v?: number): number {
  return v && v > 1 ? v : 1;
}

/**
 * Field-matches a live grant against the input describing the CURRENT
 * on-screen view: kind/username/purpose/sub for creator links, kind/tab/pill/
 * page/sub for page links. Only `status: 'active'` grants ever match —
 * revoked/expired grants must NOT short-circuit the chooser (re-minting one
 * reactivates it BE-side via upsert, so the chooser reappearing is correct).
 *
 * `window` (Task 12) is part of the match too — a grant minted for
 * general-insights/1w and one minted for general-insights/6m are DIFFERENT
 * documents (different BE lookup keys), so a grant minted under one window
 * must never be reported as "already active" for a different CURRENT window
 * selection. Only meaningful for page-kind inputs; undefined on both sides
 * for every other surface, so this is a no-op there.
 */
export function findActiveGrant(
  grants: ShareGrantDto[] | undefined,
  input: ShortShareInput,
): ShareGrantDto | undefined {
  if (!grants) return undefined;
  return grants.find((g) => {
    if (g.status !== 'active' || g.kind !== input.kind) return false;
    if (normStr(g.sub) !== normStr(input.sub)) return false;
    const inputWindow = input.kind === 'page' ? input.window : undefined;
    if (normStr(g.window) !== normStr(inputWindow)) return false;
    if (input.kind === 'creator') {
      return g.username === input.username && g.purpose === input.purpose;
    }
    return (
      g.tab === input.tab &&
      normStr(g.pill) === normStr(input.pill) &&
      normPage(g.page) === normPage(input.page)
    );
  });
}

/**
 * Shared hook for the admin-only campaign deep-link share buttons.
 *
 * Returns `{ canShare, makeUrl, makeShortUrl }`:
 * - `canShare` is true only when the admin app enabled sharing AND supplied a
 *   brand-app base URL AND a campaign param (slug/id) is known. The brand app
 *   never sets these, so every share button stays hidden there.
 * - `makeUrl(params)` builds the absolute brand-app link for the given deep-link
 *   params, folding in the current `?page` and `?pill` so the link preserves the
 *   caller's view state. Caller-provided values win (they are spread last).
 * - `makeShortUrl(input)` builds the deterministic short link (see below).
 */
export function useCampaignShare() {
  const enableSharing = useCampaignCapabilitiesStore((s) => s.enableSharing);
  const shareBrandBaseUrl = useCampaignCapabilitiesStore((s) => s.shareBrandBaseUrl);
  const campaignSlug = useCampaignUiStore((s) => s.campaignSlug);
  const campaignId = useCampaignUiStore((s) => s.campaignId);
  const campaignShortCode = useCampaignUiStore((s) => s.campaignShortCode);
  const param = campaignSlug || campaignId; // prefer the readable slug
  const canShare = !!enableSharing && !!shareBrandBaseUrl && !!param;

  const { page, pill } = useSearch({ strict: false }) as Partial<CampaignSearch>;

  const makeUrl = (search: Partial<CampaignSearch>) => {
    // The second check is implied by canShare (which already requires
    // shareBrandBaseUrl truthy) — restated so TS narrows it to `string` for
    // the rest of this closure instead of needing an `as string` cast below.
    if (!canShare || !shareBrandBaseUrl) return null;
    const merged: Partial<CampaignSearch> = {
      ...(page && page > 1 ? { page } : {}),
      ...(pill ? { pill } : {}),
      ...search,
    };
    return buildCampaignShareUrl(shareBrandBaseUrl, param, merged);
  };

  /**
   * Deterministic short link — pure string, no API. Page links fold in the
   * CURRENT ?pill/?page (the snapshot IS the shared intent); `sub` on a page
   * link (Reporting/Settings sub-tabs) becomes a readable virtual slug
   * instead of pill/page (see buildShortSharePath). Creator links carry no
   * view state (live-resolved at open). Legacy campaigns without a slug fall
   * back to the long canonical URL.
   */
  const makeShortUrl = (input: ShortShareInput): string | null => {
    if (!canShare || !shareBrandBaseUrl) return null; // see makeUrl's comment above
    const campCode = campaignShortCode || buildCampCode(campaignSlug || '');
    if (!campCode) {
      return input.kind === 'creator'
        ? makeUrl({ panel: PANEL_BY_PURPOSE[input.purpose], creator: input.username, sub: input.sub })
        : makeUrl({ tab: input.tab, sub: input.sub });
    }
    // `input` is now the authoritative source for pill/page (callers — e.g.
    // DashboardLayout's getShareInput — read it from the same live search
    // state this hook does, and it's what actually gets minted). The live
    // ?pill/?page read below only FILLS IN what `input` omits — a defensive
    // fallback for a caller that forgets to carry them, belt-and-braces
    // against `input` and live URL state ever disagreeing — so it's spread
    // first and `input` wins on conflict, matching makeUrl's same
    // caller-wins precedence above.
    const merged: ShortShareInput =
      input.kind === 'page'
        ? { ...(pill ? { pill } : {}), ...(page && page > 1 ? { page } : {}), ...input }
        : input;
    return `${shareBrandBaseUrl.replace(/\/+$/, '')}${buildShortSharePath(campCode, merged)}`;
  };

  return { canShare, makeUrl, makeShortUrl, shareInputTail, findActiveGrant };
}
